import React, { useEffect, useState } from 'react';
import { Text } from '@consta/uikit/Text';
import QRCode from 'react-qr-code';
import { Button } from '@consta/uikit/Button';
import { useFlag } from '@consta/uikit/useFlag';
import { useAtom } from '@reatom/npm-react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@consta/uikit/TextField';
import { IconCopy } from '@consta/icons/IconCopy';
import toast from 'react-hot-toast';
import { ProgressSpin } from '@consta/uikit/ProgressSpin';
import { Flex } from '##/components/Layout/Flex';
import { cnMfaConnectModal } from '../../../MfaConnectModal';
import { mfaConnect } from '##/api/actions/mfa';
import { accountIdAtom } from '##/atoms/accounts';
import { useAnchorNavigate } from '##/hooks/useAnchorNavigate';
import { useUuid } from '##/hooks/useUuid';
import { IMfaConnectModalConnectionStepProps } from '../../types';

export const TOTPQr = (props: IMfaConnectModalConnectionStepProps) => {
  const { setFormStep } = props;
  const [isLoading, setIsLoading] = useFlag();
  const [qr, setQr] = useState({ url: '', secret: '' });
  const [user_id] = useAtom(accountIdAtom);
  const [uuid] = useUuid();
  const { t } = useTranslation();
  const [navigate] = useAnchorNavigate();

  const handleClose = () => {
    setFormStep('device');
  };

  const onContinue = (e: React.MouseEvent) => {
    navigate('/multi_factor')(e);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(qr.secret);
      toast.success(t('mfa.connection.qr.success_copy'));
    } catch (err) {
      toast.error(t('mfa.connection.qr.fail_copy'));
    }
  };

  useEffect(() => {
    const fetchQr = async () => {
      setIsLoading.on();
      try {
        const {
          data: { data },
        } = await mfaConnect('google_totp', { user_id }, uuid);
        setQr({
          url: data.data.qr_uri,
          secret: data.data.secret,
        });
      } finally {
        setIsLoading.off();
      }
    };

    fetchQr();
  }, []);

  return (
    <>
      <Text as="h1" size="3xl" weight="semibold" align="center">
        {t('mfa.connection.qr.title')}
      </Text>
      <Text
        as="h3"
        size="l"
        view="ghost"
        align="center"
        weight="regular"
        className={cnMfaConnectModal('Description')}
      >
        {t('mfa.connection.qr.desc')}
      </Text>
      <Flex className={cnMfaConnectModal('QR')} align="center" justify="center">
        {!isLoading && qr.url ? (
          <QRCode value={qr.url} />
        ) : (
          <ProgressSpin size="2xl" />
        )}
      </Flex>
      <Text as="h3" size="l" view="ghost" align="center" weight="regular">
        {t('mfa.connection.qr.copy')}
      </Text>
      <Flex gap="12px" className={cnMfaConnectModal('QR__input')}>
        <TextField value={qr.secret} type="text" readOnly />
        <Button
          onClick={copyToClipboard}
          view="secondary"
          iconLeft={IconCopy}
          disabled={!qr.secret || isLoading}
        />
      </Flex>
      <Flex gap="12px" className={cnMfaConnectModal('Buttons')}>
        <Button
          onClick={handleClose}
          label={t('mfa.connection.cancel')!}
          view="secondary"
        />
        <Button
          onClick={onContinue}
          disabled={isLoading}
          label={t('mfa.connection.continue')!}
        />
      </Flex>
    </>
  );
};
